import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";
import { getWhatsappOverview, getWhatsappSupportStats } from '../../actions/whatsappAnalytics';
import DateRangeBar, { useDateRange } from './DateRangeBar';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';

const NO_VALUE = '-';
const SPARKLINE_WIDTH = 100;
const SPARKLINE_HEIGHT = 28;
const SPARKLINE_PAD = 2;

const formatCount = (value) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : NO_VALUE);

const formatMoney = (value) => (
    Number.isFinite(value)
        ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
        : NO_VALUE
);

const formatPercent = (part, whole) => (
    whole > 0 && Number.isFinite(part) ? `${Math.round((part / whole) * 1000) / 10}%` : NO_VALUE
);

const sum = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0);

const Sparkline = ({ values, label }) => {
    const points = values.length > 1 ? values : [...values, ...values];
    const max = Math.max(...points, 1);
    const step = SPARKLINE_WIDTH / Math.max(points.length - 1, 1);
    const usable = SPARKLINE_HEIGHT - SPARKLINE_PAD * 2;
    const path = points
        .map((value, index) => `${index === 0 ? 'M' : 'L'}${(index * step).toFixed(2)},${(SPARKLINE_HEIGHT - SPARKLINE_PAD - (value / max) * usable).toFixed(2)}`)
        .join(' ');

    return (
        <svg
            role="img"
            aria-label={label}
            viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
            preserveAspectRatio="none"
            width="100%"
            height={SPARKLINE_HEIGHT}
            className="text-primary"
        >
            <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

Sparkline.propTypes = {
    values: PropTypes.arrayOf(PropTypes.number).isRequired,
    label: PropTypes.string.isRequired
};

const KpiCard = ({ title, value, hint, series }) => (
    <Card className="shadow-sm h-100">
        <Card.Body>
            <div className="text-muted small">{title}</div>
            <div className="fs-3 fw-semibold">{value}</div>
            <div className="text-muted small mb-2" style={{ minHeight: '1.25rem' }}>{hint}</div>
            {series && <Sparkline values={series} label={`${title} per day`} />}
        </Card.Body>
    </Card>
);

KpiCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    hint: PropTypes.string,
    series: PropTypes.arrayOf(PropTypes.number)
};

const buildKpis = (overview, supportStats) => {
    const { messages, users, offers, daily, deliveryTracking } = overview;
    const status = messages.byStatus || {};
    const sent = messages.total - (status.dry_run || 0) - (status.failed || 0);
    const backlog = supportStats ? supportStats.backlog : null;

    const kpis = [
        { key: 'sent', title: 'Messages sent', value: formatCount(sent), series: daily.map((day) => day.sent) }
    ];

    if (deliveryTracking) {
        kpis.push(
            { key: 'delivered', title: 'Delivered', value: formatPercent((status.delivered || 0) + (status.read || 0), sent), hint: 'of messages sent' },
            { key: 'read', title: 'Read', value: formatPercent(status.read || 0, sent), hint: 'of messages sent' }
        );
    }

    kpis.push(
        { key: 'users', title: 'Users messaged', value: formatCount(users.messaged), hint: 'unique customers' },
        { key: 'replied', title: 'Replied', value: formatPercent(users.replied, users.messaged), hint: `${formatCount(users.replied)} users` },
        { key: 'tapped', title: 'Tapped a button', value: formatPercent(users.tapped, users.messaged), hint: `${formatCount(users.tapped)} users` },
        { key: 'offers', title: 'Offers made', value: formatCount(offers.offered) },
        {
            key: 'conversions',
            title: 'Conversions',
            value: formatCount(offers.paid),
            hint: `${formatPercent(offers.paid, offers.offered)} of offers`,
            series: daily.map((day) => day.paid)
        },
        { key: 'revenue', title: 'Revenue', value: formatMoney(offers.revenue), series: daily.map((day) => day.revenue) },
        {
            key: 'support',
            title: 'Open support requests',
            value: backlog ? formatCount(backlog.total) : NO_VALUE,
            hint: backlog && backlog.oldestAgeHours !== null ? `oldest ${backlog.oldestAgeHours}h` : 'open and in progress'
        }
    );

    return kpis;
};

const hasNoActivity = ({ messages, users, offers, daily }) => (
    messages.total === 0 && users.replied === 0 && offers.offered === 0 && sum(daily, 'inbound') === 0
);

const Overview = ({ overview, supportStats, error, getWhatsappOverview, getWhatsappSupportStats }) => {
    const { range, setRange } = useDateRange();

    const load = useCallback(() => {
        getWhatsappOverview({ from: range.from, to: range.to });
        getWhatsappSupportStats({ from: range.from, to: range.to });
    }, [range.from, range.to, getWhatsappOverview, getWhatsappSupportStats]);

    useEffect(() => {
        load();
    }, [load]);

    const ready = Boolean(overview && overview.range.from === range.from && overview.range.to === range.to);

    const renderBody = () => {
        if (!ready) {
            return error ? <ErrorBanner message={error} onRetry={load} /> : <LoadingState />;
        }

        const failures = overview.failures || [];
        const failureTotal = sum(failures, 'count');

        return (
            <>
                {error && <ErrorBanner variant="warning" prefix="Some figures could not be loaded: " message={error} onRetry={load} />}
                {hasNoActivity(overview) && (
                    <EmptyState>No WhatsApp activity in this range.</EmptyState>
                )}
                <Row className="g-3 mb-4">
                    {buildKpis(overview, supportStats).map(({ key, ...kpi }) => (
                        <Col key={key} xs={12} sm={6} xl={3}>
                            <KpiCard {...kpi} />
                        </Col>
                    ))}
                </Row>
                <Card className="shadow-sm">
                    <Card.Header className="bg-white fw-semibold">Failure reasons</Card.Header>
                    <Card.Body className="p-0">
                        {failures.length === 0 ? (
                            <div className="text-muted p-3">No failed messages in this range.</div>
                        ) : (
                            <Table responsive hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Failure code</th>
                                        <th className="text-end">Messages</th>
                                        <th className="text-end">Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failures.map(({ code, count }) => (
                                        <tr key={code}>
                                            <td>{code}</td>
                                            <td className="text-end">{formatCount(count)}</td>
                                            <td className="text-end">{formatPercent(count, failureTotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Overview</h2>
                    <p className="text-muted mb-0">Days are IST. Percentages are of unique users messaged unless stated.</p>
                </Col>
                <Col xs={12} lg="auto">
                    <DateRangeBar range={range} onChange={setRange} />
                </Col>
            </Row>
            {renderBody()}
        </Container>
    );
};

Overview.propTypes = {
    overview: PropTypes.object,
    supportStats: PropTypes.object,
    error: PropTypes.string,
    getWhatsappOverview: PropTypes.func.isRequired,
    getWhatsappSupportStats: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    overview: state.whatsappAnalytics.overview,
    supportStats: state.whatsappAnalytics.supportStats,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, { getWhatsappOverview, getWhatsappSupportStats })(Overview);
